import React, { useCallback, useEffect, useState } from "react";
import { FlatList, RefreshControl, View, Text } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import useWindow from "@/hooks/useWindow";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { getClaimHistory, ClaimHistoryItem } from "@/services/claims";
import { ClaimHistoryCard } from "@/components/claims/ClaimHistoryCard";

type Props = {
  isActive: boolean;
};

const ClaimHistoryTab: React.FC<Props> = ({ isActive }) => {
  const insets = useSafeAreaInsets();
  const { isDesktop } = useWindow();
  const [claimHistory, setClaimHistory] = useState<ClaimHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchClaimHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const history = await getClaimHistory();
      setClaimHistory(history);
      setHasFetched(true);
    } catch (error) {
      console.error("Error fetching claim history:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch history when tab becomes active (lazy loading)
  useEffect(() => {
    if (isActive && !hasFetched) {
      fetchClaimHistory();
    }
  }, [isActive, hasFetched, fetchClaimHistory]);

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyTitle}>No claims yet</Text>
      <Text style={styles.emptySubtitle}>
        Your filed claims will appear here
      </Text>
    </View>
  );

  return (
    <FlatList
      data={claimHistory}
      keyExtractor={(item) => item.suitId}
      showsVerticalScrollIndicator={false}
      style={isDesktop && styles.listContainer}
      contentContainerStyle={[
        isDesktop && styles.contentContainer,
        { paddingBottom: insets.bottom + 100 },
        claimHistory.length === 0 && styles.emptyListContainer,
      ]}
      renderItem={({ item }) => (
        <View style={isDesktop && styles.itemContainerDesktop}>
          <ClaimHistoryCard item={item} />
        </View>
      )}
      ListEmptyComponent={renderEmptyState}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={fetchClaimHistory}
          colors={["#4F46E5"]}
          tintColor="#4F46E5"
        />
      }
    />
  );
};

export default ClaimHistoryTab;

const styles = StyleSheet.create({
  listContainer: {
    width: "100%",
  },
  contentContainer: {
    alignItems: "center",
  },
  itemContainerDesktop: {
    width: 600,
  },
  emptyListContainer: {
    flex: 1,
    justifyContent: "center",
  },
  emptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: Colors.text,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.InterTight.Regular,
    color: "#6B7280",
    textAlign: "center",
  },
});
